import {useJobContext} from 'contexts/JobContext';
import {useNotificationContext} from 'contexts/NotificationContext';
import {useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {submitJob} from 'service/JobService';
import {genericMessageHandler} from 'shared/helpers';
import {sendAnalyticsEvent} from 'shared/helpers/Analytics';
import {PartialJob} from 'shared/types/PartialJob';
import {Event} from 'shared/events/Event';
import {AxiosError} from 'axios';
import {useJobVariations} from 'shared/components/Header/helper/useJobVariations';
import {JOB_STATUS} from 'components/customer/Dashboard/LastJobButton';
import {
    invalidateJobCost,
    useLazyGetJobQuery,
} from 'components/customer/Job/store/jobApi';
import {cloneDeep} from 'lodash';
import {DispatchMethod} from 'hooks/Job/Job';
import {useAppDispatch} from 'store/customer';
import {useAppContext} from 'contexts/ApplicationContext';
import {useCurrentDispatchMethod} from 'components/customer/Job/helpers/useCurrentDispatchMethods';

export const useSubmitJob = (noRedirect = false): Event => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {notify} = useNotificationContext();
    const {job, setJob, setRoom} = useJobContext() as PartialJob;
    const {userProfile} = useAppContext();
    const {noVariations} = useJobVariations();
    const [getJobDetails] = useLazyGetJobQuery();
    const dispatchMethod = useCurrentDispatchMethod();

    const action = useCallback(async () => {
        if (job.totalProductCount == 0) {
            throw new Error('Job should have at least one product in it');
        }

        sendAnalyticsEvent('submitted', 'co_job');
        try {
            const response = await submitJob(job.id);
            response.map((m) =>
                genericMessageHandler(notify, {message: m}, 'success')
            );

            if (noRedirect) {
                const {data} = await getJobDetails({
                    jobId: job.id,
                    timestamp: Date.now(), // this is used to force a re-fetch. It's not used in the API
                });

                if (data) {
                    setJob(cloneDeep(data));
                }
            }
            setRoom({});
            dispatch(invalidateJobCost());

            if (!noRedirect) {
                const jobConfirmationLandingPageEnabled =
                    userProfile?.jobSubmittedPageEnabled;

                const hasFreight = dispatchMethod != DispatchMethod.PICKUP;

                if (
                    job.status == JOB_STATUS.PROCESSING &&
                    (!noVariations || hasFreight) &&
                    jobConfirmationLandingPageEnabled
                ) {
                    const params = new URLSearchParams();
                    if (!noVariations) {
                        params.append('variations', '1');
                    }

                    if (hasFreight) {
                        params.append('freight', '1');
                    }

                    void navigate({
                        pathname: `/v2/job/${job.displayId}/job-confirmation`,
                        search: `?${params.toString()}`,
                    });
                } else {
                    // redirect to home page
                    void navigate(`/v2/job/${job.displayId}/success`);
                }
            }
        } catch (error) {
            let errors = ['Error submitting the job. Please try again later.'];

            if (typeof error === 'object' && error !== null) {
                const axiosError = error as AxiosError;
                if ('response' in axiosError) {
                    if (
                        'data' in axiosError.response &&
                        typeof axiosError.response.data == 'object'
                    ) {
                        if (
                            'errors' in axiosError.response.data &&
                            Array.isArray(axiosError.response.data.errors)
                        ) {
                            errors = axiosError.response.data
                                .errors as string[];
                        } else if ('error' in axiosError.response.data) {
                            errors = [axiosError.response.data.error as string];
                        }
                    }
                } else if ('message' in axiosError) {
                    errors = [axiosError.message];
                }
            }

            if (!Array.isArray(errors)) {
                errors = [errors];
            }

            errors.forEach((error) => {
                genericMessageHandler(notify, {message: error});
            });
        }
    }, [job, noVariations, userProfile, dispatchMethod]);

    return {
        action,
    };
};
